// coverageLevels.js — TEST 3.10 (patient half). THE ONE READER of deductible and
// out-of-pocket figures in this app. Gate: scripts/check-coverage-level-labels.mjs.
//
// ★★ WHY A FIGURE NEEDS ITS LEVEL. A payer reports every deductible / out-of-pocket line
//   per coverage level — IND (individual) and FAM (family). Measured on a real Kaiser
//   response (board row TEST 3.10): IND max 1500 / remaining 1475, FAM max 3000 / remaining
//   2940.14. orella-api files them under eligibility.spendDown.<kind>.<individual|family>
//   (rcm_service, since v227.13.0). The FLAT top-level fields (deductibleCents,
//   deductibleRemainingCents, outOfPocketMaxCents, outOfPocketRemainingCents) carry no
//   level, and a result stored before the API half (B7) took the first line in payer order
//   for each of them separately — so a stored flat TOTAL can be the family figure while the
//   flat REMAINING is the individual one. Read as a pair, that said "$1,525 of $3,000 met".
//
// ★ THE RULE: a figure is read from spendDown only, and always WITH its level. The
//   individual figures first; the family figures only when the plan reports no individual
//   figure for that measure. A total and a remaining are always taken from the SAME level
//   (never one from each) and from the SAME network bucket of it (S1, below). A result with no spendDown at all (stored before v227.13.0)
//   has no figure whose level is known, so it shows none — a recheck writes a new result.

const LEVELS = ['individual', 'family']

export const LEVEL_KEYS = {
  individual: 'eligibility.levelIndividual',
  family: 'eligibility.levelFamily',
}

// ★★ S1 (P3 review): ONE NETWORK BUCKET PER LEVEL, TOO. Within a level, rcm_service._enrich_271
//   files every figure under the network it came from (inNetwork / unknown / outOfNetwork) AND
//   copies it to the level's flat total / remaining — the flat field takes the in-network figure
//   when there is one, else whichever network reported it first. So the flat pair can be an
//   in-network total beside an out-of-network remaining. A total and a remaining are therefore
//   taken from ONE network bucket — in-network, else unknown-network, else out-of-network — and
//   the level's flat fields are not read (they carry no network). `network` says which bucket
//   it was ('in' | 'unknown' | 'out'); EligibilityBadge's meter draws its network chip from it (PROD-682).
const NETWORK_BUCKETS = [['inNetwork', 'in'], ['unknown', 'unknown'], ['outOfNetwork', 'out']]

// {level, network, total, remaining} at one level, or null when no network bucket of that
// level reports either figure. `kind` is 'deductible' or 'outOfPocket'.
export function spendAtLevel(eligibility, kind, level) {
  const sd = eligibility?.spendDown?.[kind]?.[level]
  if (!sd || typeof sd !== 'object') return null
  for (const [key, network] of NETWORK_BUCKETS) {
    const b = sd[key]
    if (!b || typeof b !== 'object') continue
    const total = b.total ?? null
    const remaining = b.remaining ?? null
    if (total == null && remaining == null) continue
    return { level, network, total, remaining }
  }
  return null
}

// The one figure pair a surface shows for a measure: {level, total, remaining} or null.
export function pickLevelSpend(eligibility, kind) {
  for (const level of LEVELS) {
    const s = spendAtLevel(eligibility, kind, level)
    if (s) return s
  }
  return null
}

// Every level that reports the measure, individual first (for text that can name both).
export function allLevelSpends(eligibility, kind) {
  return LEVELS.map((level) => spendAtLevel(eligibility, kind, level)).filter(Boolean)
}
